import type { VoidFunctionComponent } from 'react';
import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import usePaymentMethod from './usePaymentMethod';
import Loader from '../../../generic/Loader';
import { window } from '../../../../globals';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, Icon, icons } from '../../../generic';
import Flex from '../../../generic/Flex';
import Text from '../../../generic/Text';
import styles from '../style.module.less';

const navigateToCheckout = (url: string) => {
  window.location = url;
};

const getStatus = () => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('status');
};

const PaymentMethod: VoidFunctionComponent = () => {
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const statusValue = getStatus();
    setStatus(statusValue);
    if (statusValue) {
      // Remove the `status` parameter from the URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('status');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []);

  const {
    isLoading,
    data,
  } = usePaymentMethod();
  if (isLoading) {
    return (<Loader />);
  }

  if (!data?.sessionUrl) {
    return null;
  }

  return (
    <>
      <Flex direction="column" className={styles.paymentMethodContainer}>
        {data?.paymentMethod ?
          <>
            <p>Card Brand: <b>{data.paymentMethod?.card?.brand}</b></p>
            <p>Last 4 Digits: <b>{data.paymentMethod?.card?.last4}</b></p>
            <p>
              Expiry: <b>{data.paymentMethod?.card?.exp_month}/{data.paymentMethod?.card?.exp_year}</b>
            </p>
          </>
          :
          <>
            <span className={styles.paymentNotFoundIcon}>
              <Icon icon={icons.remove} />
            </span>
            <Text.H2>Not found</Text.H2>
            <p>
              No payment method is currently associated with your account.
            </p>
          </>}
        {data.sessionUrl &&
          <Button onClick={() => navigateToCheckout(data.sessionUrl as string)} size={BUTTON_SIZE.MEDIUM}>
            {data?.paymentMethod ? 'Edit' : 'Add Payment Method'}
          </Button>}
      </Flex>

      <DisplayConfirmation status={status} onHide={() => setStatus(null)} />
    </>
  );
};

type DisplayModalProps = {
  onHide: () => void;
  status: string | null;
};

const DisplayConfirmation: VoidFunctionComponent<DisplayModalProps> = ({
  onHide,
  status,
}) => {
  if (status !== 'succeeded') {
    return null;
  }
  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          Payment method was updated!
        </Modal.Title>
      </Modal.Header>
      <Modal.Footer />
    </Modal>
  );
};

export default PaymentMethod;
